<template>
  <div style="display: none;">
    <!-- Tooltip element is created dynamically in mounted() -->
  </div>
</template>

<script>
import { useLabPath } from '../composables/useLabPath.js'

export default {
  name: 'CurveSelectionTooltip',
  props: {
    fromSource: {
      type: String,
      default: null,
      description: 'Source route to include in navigation (e.g., "curve-results")'
    }
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
    return {
      tooltipEl: null
    }
  },
  mounted() {
    this.createTooltip()
  },
  beforeUnmount() {
    this.cleanupTooltip()
  },
  methods: {
    createTooltip() {
      // Create single tooltip instance if it doesn't exist
      if (this.tooltipEl) {
        console.log('📊 Tooltip already exists, skipping creation')
        return
      }
      
      console.log('📊 Creating shared tooltip element')
      this.tooltipEl = document.createElement('div')
      this.tooltipEl.className = 'chartjs-tooltip'
      this.tooltipEl.style.cssText = `
        position: fixed;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        border-radius: 6px;
        padding: 12px;
        font-size: 12px;
        pointer-events: auto;
        z-index: 1000;
        max-width: 300px;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        opacity: 0;
      `
      document.body.appendChild(this.tooltipEl)
      
      // Add mouse enter/leave events to the tooltip
      this.tooltipEl.addEventListener('mouseenter', () => {
        this.tooltipEl.dataset.hovering = 'tooltip'
        this.tooltipEl.style.opacity = '1'
      })
      
      this.tooltipEl.addEventListener('mouseleave', () => {
        this.tooltipEl.dataset.hovering = 'false'
        this.tooltipEl.style.opacity = '0'
      })
      
      // Add click event delegation for curve navigation
      this.tooltipEl.addEventListener('click', (event) => {
        const curveElement = event.target.closest('[data-curve-index]')
        if (curveElement) {
          const curveIndex = parseInt(curveElement.dataset.curveIndex)
          this.navigateToExplorer(curveIndex)
        }
      })
      
      console.log('📊 Shared tooltip created')
    },
    
    cleanupTooltip() {
      if (this.tooltipEl) {
        this.tooltipEl.remove()
        this.tooltipEl = null
      }
    },
    
    // Method to update tooltip content
    updateTooltipContent(content) {
      if (this.tooltipEl) {
        this.tooltipEl.innerHTML = content
      }
    },
    
    // Method to position tooltip
    positionTooltip(x, y) {
      if (this.tooltipEl) {
        this.tooltipEl.style.left = x + 'px'
        this.tooltipEl.style.top = (y - this.tooltipEl.offsetHeight - 10) + 'px'
        this.tooltipEl.style.opacity = '1'
        this.tooltipEl.style.pointerEvents = 'auto'
        this.tooltipEl.dataset.hovering = 'true'
      }
    },
    
    // Method to hide tooltip
    hideTooltip() {
      if (this.tooltipEl && this.tooltipEl.dataset.hovering !== 'tooltip') {
        this.tooltipEl.style.opacity = '0'
        this.tooltipEl.style.pointerEvents = 'none'
        this.tooltipEl.dataset.hovering = 'false'
      }
    },
    
    // Navigate to Explorer in same tab
    navigateToExplorer(curveIndex) {
      try {
        console.log(`✅ Navigating to Explorer in same tab for curve ${curveIndex}`)
        // Build query string with optional from parameter
        let queryString = `curve=${curveIndex}`
        if (this.fromSource) {
          queryString += `&from=${this.fromSource}`
        }
        this.$router.push(this.toLab(`/explorer?${queryString}`))
      } catch (error) {
        console.error('❌ Error navigating to Explorer:', error)
      }
    },
    
    // Helper method to create curve list HTML
    createCurveListHtml(curveIndices) {
      if (!curveIndices || curveIndices.length === 0) {
        return '<div>No curves</div>'
      }
      
      return curveIndices.map(curveIndex => 
        `<div style="margin: 2px 0; cursor: pointer; color: #60a5fa; text-decoration: underline;" 
             data-curve-index="${curveIndex}">
           Curve ${curveIndex + 1}
         </div>`
      ).join('')
    },
    
    // Helper method to create standard tooltip HTML structure
    createTooltipHtml(value, curveIndices, category = null, count = null) {
      const curveListHtml = this.createCurveListHtml(curveIndices)
      
      let categoryText = ''
      if (category && count !== null) {
        const categoryDisplay = {
          'valid': 'Valid',
          'invalidByThisFlag': 'Invalid (by this flag)',
          'invalidByOtherFlag': 'Invalid (by other flag)'
        }
        categoryText = `
          <div style="font-size: 11px; margin-bottom: 6px; color: #9ca3af;">
            Category: ${categoryDisplay[category] || category} | Count: ${count}
          </div>
        `
      }
      
      return `
        <div style="font-weight: bold; margin-bottom: 8px;">
          Value: ${Number(value).toFixed(6)}
        </div>
        ${categoryText}
        <div style="max-height: 120px; overflow-y: auto; border: 1px solid #374151; border-radius: 4px; padding: 4px;">
          ${curveListHtml}
        </div>
      `
    }
  }
}
</script>

<style scoped>
/* No styles needed - tooltip is positioned absolutely */
</style>
